// components/ModelGallery.jsx
import React, { useEffect, useState, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import "../styles/ModelGallery.css";

const ModelGallery = () => {
    const [galleryImages, setGalleryImages] = useState([]);
    const [layoutMode, setLayoutMode] = useState("grid"); // "hero" | "grid"
    const [loading, setLoading] = useState(true);
    const sectionRef = useRef(null);

    useEffect(() => {
        const fetchGalleryData = async () => {
            try {
                const configRef = doc(db, "site_config", "home_content");
                const configSnap = await getDoc(configRef);

                if (configSnap.exists()) {
                    const data = configSnap.data();
                    if (data.galleryImages && data.galleryImages.length > 0) {
                        setGalleryImages(data.galleryImages);
                    }
                    if (data.galleryLayoutMode) {
                        setLayoutMode(data.galleryLayoutMode);
                    }
                }
            } catch (err) {
                console.error("Error fetching gallery data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchGalleryData();
    }, []);

    // Scroll-triggered reveal
    useEffect(() => {
        if (!sectionRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    sectionRef.current.classList.add("gallery-visible");
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, [loading]);

    if (loading || galleryImages.length === 0) return null;

    // ── Hero layout: single large image spanning full width ──────────────────
    if (layoutMode === "hero") {
        const heroImg = galleryImages[0];
        return (
            <section ref={sectionRef} className="model-gallery-section gallery-hero">
                <div className="gallery-hero-wrapper">
                    <img
                        src={heroImg.src}
                        alt={heroImg.alt || "Featured Model"}
                        className="gallery-hero-image"
                        style={{ objectPosition: heroImg.position || "center top" }} // Dynamically applies focal point
                    />
                    {heroImg.caption && (
                        <div className="gallery-hero-caption">
                            <span>{heroImg.caption}</span>
                        </div>
                    )}
                    <div className="gallery-hero-overlay" />
                </div>
            </section>
        );
    }

    // ── Grid layout: side-by-side images spanning the page ───────────────────
    // Support "tall" span for images that should span 2 rows
    return (
        <section ref={sectionRef} className="model-gallery-section gallery-grid">
            <div className="gallery-grid-wrapper">
                {galleryImages.map((img, i) => (
                    <div
                        key={i}
                        className={`gallery-grid-item ${img.span === "tall" ? "gallery-item-tall" : ""}`}
                        style={{ "--i": i }}
                    >
                        <img
                            src={img.src}
                            alt={img.alt || `Model ${i + 1}`}
                            className="gallery-grid-image"
                            style={{ objectPosition: img.position || "center top" }} // Dynamically applies focal point
                        />
                        {img.caption && (
                            <div className="gallery-item-caption">
                                <span>{img.caption}</span>
                            </div>
                        )}
                        <div className="gallery-item-overlay" />
                    </div>
                ))}
            </div>
        </section>
    );
};

export default ModelGallery;